/**
 * Platzierung eines Popover-Panels am Auslöser (components/ui.tsx → Popover).
 * Bewusst als reine Funktion: die Fälle, die auf dem Handy schiefgehen —
 * kein Platz nach unten, Panel läuft über den Viewport-Rand — lassen sich so
 * testen, statt sie nur im Browser zu vermuten.
 */

export type Rect = { top: number; bottom: number; left: number; right: number };
export type Size = { width: number; height: number };
export type Viewport = { width: number; height: number };
export type Align = "start" | "end";

/** Ergebnis in fixed-Koordinaten; genau eines von top/bottom ist gesetzt. */
export type Placement = { left: number; top?: number; bottom?: number; maxHeight: number };

/** Mindestabstand zum Viewport-Rand */
export const EDGE = 8;
/** Abstand zwischen Auslöser und Panel */
export const GAP = 6;
/** Darunter lohnt das Aufklappen zur einen Seite nicht mehr */
export const MIN_HEIGHT = 140;

/** Auslöser aus dem Blick gescrollt — dann hat das Panel keinen Bezug mehr. */
export function isTriggerOffscreen(trigger: Rect, viewport: Viewport): boolean {
  return trigger.bottom < 0 || trigger.top > viewport.height;
}

export function placePopover(
  trigger: Rect,
  panel: Size,
  viewport: Viewport,
  align: Align,
): Placement {
  const below = viewport.height - trigger.bottom - GAP - EDGE;
  const above = trigger.top - GAP - EDGE;
  // Nach oben klappen, wenn unten zu wenig Platz ist UND oben mehr
  const flip = below < Math.min(panel.height, MIN_HEIGHT) && above > below;

  const wanted = align === "end" ? trigger.right - panel.width : trigger.left;
  // Zuerst am rechten Rand deckeln, dann am linken: bei einem Panel, das breiter
  // als der Viewport ist, gewinnt der linke Rand (sichtbarer Anfang statt Ende).
  const maxLeft = Math.max(EDGE, viewport.width - panel.width - EDGE);

  return {
    left: Math.min(Math.max(EDGE, wanted), maxLeft),
    top: flip ? undefined : trigger.bottom + GAP,
    bottom: flip ? viewport.height - trigger.top + GAP : undefined,
    maxHeight: Math.max(MIN_HEIGHT, flip ? above : below),
  };
}
